import type { Action } from 'redux';
import { call, put } from 'redux-saga/effects';

export type GeneratorPayloadT<TPayload> = {
  payload: TPayload;
  meta: {
    resolve: (...args: any[]) => void;
    reject: (...args: any[]) => void;
  };
} & Action;

export type ApiActionT<TRequestPayload, TResponsePayload> = (
  payload: TRequestPayload,
) => Promise<TResponsePayload>;

export type ResultMetaT<TRequestPayload> = {
  request: TRequestPayload;
};

export type ResultSucceededActionT<TRequestPayload, TResponsePayload> = (
  payload: TResponsePayload,
  meta?: ResultMetaT<TRequestPayload>,
) => Action;

export type ResultFailedActionT<TRequestPayload, TResponsePayload> = (
  payload: Error,
  meta?: ResultMetaT<TRequestPayload>,
) => Action;

export function sagaApiGenWithPromise<TRequestPayload, TResponsePayload>(
  apiAction: ApiActionT<TRequestPayload, TResponsePayload>,
  resultSucceededAction: ResultSucceededActionT<
    TRequestPayload,
    TResponsePayload
  >,
  resultFailedAction: ResultFailedActionT<TRequestPayload, TResponsePayload>,
  hasPromise = true,
) {
  return function* (action: GeneratorPayloadT<TRequestPayload>) {
    try {
      const result: TResponsePayload = yield call(apiAction, action.payload);

      if (hasPromise) {
        if (action?.meta?.resolve) {
          yield call(action.meta.resolve, result);
        } else {
          console.warn(
            "action.meta.resolve was not set in action, consider using sagaApiGen if you don't need promisify your dispatches.",
          );
        }
      }
      yield put(resultSucceededAction(result, { request: action.payload }));
    } catch (exception: any) {
      console.warn(exception);
      if (hasPromise) {
        if (action?.meta?.reject) {
          // Something went wrong. We reject the promise.
          yield call(action.meta.reject, exception);
        } else {
          console.warn(
            "action.meta.reject was not set in action, consider using sagaApiGen if you don't need promisify your dispatches.",
          );
        }
      }
      yield put(resultFailedAction(exception, { request: action.payload }));
    }
  };
}

export function sagaApiGen<TRequestPayload, TResponsePayload>(
  apiAction: ApiActionT<TRequestPayload, TResponsePayload>,
  resultSucceededAction: ResultSucceededActionT<
    TRequestPayload,
    TResponsePayload
  >,
  resultFailedAction: ResultFailedActionT<TRequestPayload, TResponsePayload>,
) {
  return sagaApiGenWithPromise<TRequestPayload, TResponsePayload>(
    apiAction,
    resultSucceededAction,
    resultFailedAction,
    false,
  );
}

type PromiseResolveRejectT<TValue> = (value: TValue) => void;

export function bindActionToPromise<
  TActionPayload,
  TResolveValue = any,
  TRejectValue = any,
>(
  dispatch: (action: Action) => void,
  actionCreator: (
    payload?: TActionPayload,
    resolve?: PromiseResolveRejectT<TResolveValue>,
    reject?: PromiseResolveRejectT<TRejectValue>,
  ) => Action,
) {
  return (payload?: TActionPayload) => {
    return new Promise<TResolveValue>((resolve, reject) =>
      dispatch(actionCreator(payload, resolve, reject)),
    );
  };
}
